/**
 * Desktop/browser notification preferences and delivery.
 *
 * Split out of the settings page because the preferences were previously written there and read
 * nowhere — toggling "New mail" off had no effect on anything. This module is the single place
 * both the settings UI and the notification call sites agree on. The values themselves now live
 * in the saved Mail preferences (lib/preferences.ts), so they follow the user to every device.
 */

import { readPreferences, updatePreferences } from "./preferences";

export interface NotifPrefs {
    newMail: boolean;
    sound: boolean;
    showSender: boolean;
}

/** The pre-redesign browser key; lib/preferences.ts migrates it once. */
export const NOTIF_PREFS_KEY = "fg:mail:notif-prefs";

export function defaultNotifPrefs(): NotifPrefs {
    return { newMail: true, sound: false, showSender: true };
}

export function loadNotifPrefs(): NotifPrefs {
    return { ...readPreferences().notifications };
}

export function saveNotifPrefs(p: NotifPrefs) {
    updatePreferences({ notifications: p });
}

/** Short, quiet chime for the optional notification sound, as a data URI so it needs no asset. */
function playChime() {
    try {
        const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.0001, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
        osc.start();
        osc.stop(ctx.currentTime + 0.3);
        osc.onended = () => void ctx.close();
    } catch {
        /* autoplay blocked or Web Audio unavailable — the visual notification still fires */
    }
}

/**
 * Show a system notification.
 *
 * Prefers the Electron desktop bridge when running inside the Mail shell, and otherwise
 * falls back to the standard Web Notification API. That fallback is the fix for real notifications
 * never appearing in a browser tab: this used to call `window.fg?.notify(...)` only, which is
 * `undefined` outside the desktop app, so every new-mail notification was silently dropped while
 * the settings page's "Send test notification" button — which called `new Notification(...)`
 * directly — worked fine and made it look like notifications were configured correctly.
 */
export function pushDesktopNotification(title: string, body: string, options?: { sound?: boolean }): void {
    if (typeof window === "undefined") return;

    if (window.fg?.isDesktop) {
        window.fg.notify(title, body);
        if (options?.sound) playChime();
        return;
    }

    try {
        if (!("Notification" in window) || Notification.permission !== "granted") return;
        const n = new Notification(title, { body, icon: "/icons/icon-192.png" });
        // Browsers cap how long a notification stays up anyway, but closing it explicitly keeps a
        // stack of them from accumulating on platforms that don't auto-dismiss.
        setTimeout(() => n.close(), 8000);
        if (options?.sound) playChime();
    } catch {
        /* notification construction can throw on some mobile browsers — never break the caller */
    }
}
